import { type TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { ReactNode } from 'react';
import { Outlet } from 'react-router-dom';

import { useTypedParams } from '@sorare/routing';

import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';

import { GameViewHeader } from 'components/scoutingCenter/gameCenter/GameViewHeader';

import type {
  ScoutingCenterDayGameIdLayoutQuery,
  ScoutingCenterDayGameIdLayoutQueryVariables,
} from './__generated__/index.graphql';

const query = gql`
  query ScoutingCenterDayGameIdLayoutQuery($gameId: ID!) {
    anyGame(id: $gameId) {
      id
      ...GameViewHeader_anyGameInterface
    }
  }
  ${GameViewHeader.fragments.anyGameInterface}
` as TypedDocumentNode<
  ScoutingCenterDayGameIdLayoutQuery,
  ScoutingCenterDayGameIdLayoutQueryVariables
>;

type Tab = {
  label: ReactNode;
  to: string;
  matches?: (string | undefined | null | false)[];
};

export const loaderFn = <
  Params extends {
    day: string;
    gameId: string;
  },
>() =>
  queryLoader<Params>(
    query,
    ({ gameId }: Params) => ({
      gameId: `Game:${idFromObject(gameId)}`,
      gameIdWithoutPrefix: idFromObject(gameId),
    }),
    { preventTransitionBeforeLoaded: true }
  );

export const GameCenterDayGameIdLayout = <
  Params extends {
    day: string;
    gameId: string;
  },
>({
  tabs,
}: {
  tabs: Tab[];
}) => {
  const { day, gameId } = useTypedParams<Params>();
  const { generateSportContextPath } = useSportContext();
  const isDesktop = useIsDesktopAndAbove();

  const { data } = useQueryLoader<ScoutingCenterDayGameIdLayoutQuery>();
  const game = data?.anyGame;

  return (
    <Vertical>
      {!!game && (
        <ContentWithStickyHeader
          header={
            <ConditionalWrapper
              Wrapper={GameViewHeader}
              wrap={!isDesktop}
              props={{ game }}
            >
              <TabBar
                variant="flat"
                transparent
                fullWidth
                ignoreQueryParams
                className={classNames({ center: !isDesktop })}
              >
                {tabs.map(({ label, to, matches }) => (
                  <Tab
                    key={to}
                    matches={matches}
                    to={generateSportContextPath(to, {
                      params: { day, gameId },
                    })}
                  >
                    {label}
                  </Tab>
                ))}
              </TabBar>
            </ConditionalWrapper>
          }
        >
          <Outlet />
        </ContentWithStickyHeader>
      )}
    </Vertical>
  );
};
